import { useState } from "react";
import { useSelector } from "react-redux";
import {
  PreRegistroVisitantesService,
  PreRegistroVisitantesVerificacionTelefonoService,
} from "../services/PreRegistroVisitantesService";
import { notifyInfo } from "jsx/components/CitoPlus/components/CitoPlusToastr";
import { selectPermission } from "../store/selectors/PermissionSelectors";

export const usePreRegistroVisitantes = () => {
  const [loading, setLoading] = useState(false);
  const [lastSearchedId, setLastSearchedId] = useState("");

  const tienePermiso = useSelector(selectPermission("pre_registro_visitante"));
  /**
   * Consulta pre-registro por identificación y llena automáticamente los campos del formulario
   * @param {string} identificacion - Número de cédula/identificación
   * @param {function} setFieldValue - Función de Formik para actualizar campos
   * @param {object} options - Opciones adicionales
   * @returns {object|null} - Datos del visitante o null si no se encuentra
   */
  const cargarDatosPreRegistro = async (
    identificacion,
    setFieldValue,
    options = {},
  ) => {
    if (!tienePermiso) return null;

    // Evitar consultas repetidas de la misma identificación
    if (identificacion === lastSearchedId) return null;

    if (
      !identificacion ||
      identificacion.length < 7 ||
      !/^[0-9]+$/.test(identificacion)
    ) {
      return null;
    }

    setLoading(true);
    setLastSearchedId(identificacion);

    try {
      const data =
        await PreRegistroVisitantesService.getByIdentificacion(identificacion);

      if (data) {
        // Llenar campos comunes
        if (data.nombres) {
          setFieldValue("nombre", data.nombres);
        }

        if (data.autorizado_por) {
          setFieldValue("autorizado_por", data.autorizado_por);
        }

        if (data.inmueble_name) {
          // El formulario espera solo el string del nombre del inmueble
          setFieldValue("inmueble", data.inmueble_name);
        }

        if (data.nota) {
          setFieldValue("nota", data.nota);
        }

        if (data.arl) {
          setFieldValue("arl", data.arl);
        }

        if (data.eps) {
          setFieldValue("eps", data.eps);
        }

        // Tipo de ingreso con formato para select
        if (data.tipo_de_ingreso !== undefined) {
          setFieldValue("tipo_de_ingreso", {
            value: data.tipo_de_ingreso.toString(),
            label: data.tipo_de_ingreso_display || "VISITANTE",
          });
        }

        // Campos específicos para vehículos si existen
        if (data.placa) {
          setFieldValue("placa", data.placa);
        }

        if (data.marca_vehiculo) {
          setFieldValue("marca_vehiculo", data.marca_vehiculo);
        }

        if (data.modelo_vehiculo) {
          setFieldValue("modelo_vehiculo", data.modelo_vehiculo);
        }

        if (data.color_vehiculo) {
          setFieldValue("color_vehiculo", data.color_vehiculo);
        }

        // Campos adicionales vehiculares si existen
        if (data.modelo) {
          setFieldValue("modelo", data.modelo);
        }

        if (data.color) {
          setFieldValue("color", data.color);
        }

        // Manejar datos de facturación electrónica
        if (data.factura_electronica) {
          const factura = data.factura_electronica;

          // Activar checkbox y llenar campos si hay email o teléfono
          if (factura.correo || factura.whatsapp) {
            setFieldValue("requiere_factura", true);

            if (factura.correo) {
              setFieldValue("email", factura.correo);
            }

            if (factura.whatsapp) {
              setFieldValue("telefono", factura.whatsapp);
            }
          }
        }

        // Notificación solo si no está en modo silencioso
        if (!options.silencioso) {
          notifyInfo("Datos cargados desde pre-registro");
        }

        return data;
      }

      return null;
    } catch (error) {
      // Error completamente silencioso - no molestar al usuario
      console.warn("Error consultando pre-registro:", error);
      return null;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Limpiar el estado interno del hook
   */
  const limpiarEstado = () => {
    setLastSearchedId("");
    setLoading(false);
  };

  /**
   * Verificar si una identificación es válida para consultar
   * @param {string} identificacion - Número de identificación
   * @returns {boolean} - True si es válida para consultar
   */
  const esIdentificacionValida = (identificacion) => {
    return !!(
      identificacion &&
      identificacion.length >= 7 &&
      identificacion.length <= 15 &&
      /^[0-9]+$/.test(identificacion)
    );
  };

  /**
   * Maneja la identificación desde múltiples fuentes (manual, lector de códigos)
   * Prioriza pre-registro sobre datos del lector de códigos de barras
   * @param {string} cedula - Número de cédula/identificación
   * @param {function} setFieldValue - Función de Formik para actualizar campos
   * @param {string} source - Fuente: 'manual' | 'barcode'
   * @param {object} options - Opciones adicionales
   * @returns {object|null} - Datos encontrados o null
   */
  const handleIdentificacionChange = async (
    cedula,
    setFieldValue,
    source = "manual",
    options = {},
  ) => {
    if (!esIdentificacionValida(cedula)) return null;

    try {
      // 1. Intentar pre-registro primero (siempre tiene prioridad)
      const preRegistroData = await cargarDatosPreRegistro(
        cedula,
        setFieldValue,
        {
          silencioso: source === "barcode" || options.silencioso,
        },
      );

      if (preRegistroData) {
        return { source: "pre-registro", data: preRegistroData };
      }

      // 2. Si no hay pre-registro y viene del lector, permitir que LectorDeBarras procese
      if (source === "barcode") {
        // LectorDeBarras ya habrá procesado los datos de la cédula si están disponibles
        return { source: "barcode-fallback", data: null };
      }

      return null;
    } catch (error) {
      console.warn("Error en handleIdentificacionChange:", error);
      return null;
    }
  };

  /**
   * Valida el teléfono usando el endpoint de verificación para determinar si puede hacer pre-registro y activar Airbnb
   * @param {string} telefono - Número de teléfono a validar
   * @param {string} codigo - Código del inmueble
   * @returns {object|null} - Datos de validación o null si hay error
   */
  const validarTelefono = async (telefono, codigo) => {
    try {
      const data =
        await PreRegistroVisitantesVerificacionTelefonoService.getByTelefono(
          telefono,
          codigo,
        );
      return data;
    } catch (error) {
      console.warn("Error validando teléfono:", error);
      return null;
    }
  };

  return {
    cargarDatosPreRegistro,
    handleIdentificacionChange,
    validarTelefono,
    loading,
    tienePermiso,
    limpiarEstado,
    esIdentificacionValida,
  };
};

export default usePreRegistroVisitantes;
